import type { StatusDefinition } from "@kontextwerk/web-sdk/core/display/status"
import {
  mdiAlertCircleOutline,
  mdiCalendarCheckOutline,
  mdiCalendarPlusOutline,
  mdiCheckCircleOutline,
  mdiClipboardAlertOutline,
  mdiEyeCheckOutline,
  mdiFileDocumentAlertOutline,
  mdiProgressClock,
} from "@mdi/js"
import { buildRichTooltip, tooltipText } from "../../../../../_shared/_logic/rich-tooltips"
import type { WorkOrderAnnouncementOverview } from "../announcement-overview"
import { buildLetterDeliveryWindowPresentation } from "./_logic/letterDeliveryWindow"

export type WorkOrderActionRequiredReason =
  | "appointment_missing"
  | "announcement_plan_missing"
  | "letter_delivery_required"
  | "letter_delivery_pending"
  | "announcement_attention"
  | "announcement_completed"
  | "appointment_confirmed"
  | "announcement_running"
  | "work_order_closed"
  | "none"

export type WorkOrderActionRequiredState = "required" | "not_yet_required" | "not_required"

export type WorkOrderActionRequiredProjection = {
  required?: boolean | null
  state?: string | null
  reason?: string | null
  priority?: number | null
  required_at?: string | null
}

export type WorkOrderActionRequiredOverview = {
  required: boolean
  state: WorkOrderActionRequiredState
  reason: WorkOrderActionRequiredReason
  label: string
  subLabel: string
  definition: StatusDefinition<string>
  tooltip: string
  searchText: string
}

const knownReasons = new Set<WorkOrderActionRequiredReason>([
  "appointment_missing",
  "announcement_plan_missing",
  "letter_delivery_required",
  "letter_delivery_pending",
  "announcement_attention",
  "announcement_completed",
  "appointment_confirmed",
  "announcement_running",
  "work_order_closed",
  "none",
])

const knownStates = new Set<WorkOrderActionRequiredState>([
  "required",
  "not_yet_required",
  "not_required",
])

const normalizeReason = (value: unknown): WorkOrderActionRequiredReason => {
  const normalized = `${value ?? ""}`.trim() as WorkOrderActionRequiredReason

  return knownReasons.has(normalized) ? normalized : "none"
}

const normalizeState = (
  actionRequired?: WorkOrderActionRequiredProjection | null,
): WorkOrderActionRequiredState => {
  const normalized = `${actionRequired?.state ?? ""}`.trim() as WorkOrderActionRequiredState

  if (knownStates.has(normalized)) return normalized

  if (actionRequired?.required === true) return "required"

  if (Number(actionRequired?.priority ?? 0) > 0) return "not_yet_required"

  return "not_required"
}

const buildMissingProjectionFallback = ({
  actionRequired,
  appointmentLabel,
  announcementOverview,
}: {
  actionRequired?: WorkOrderActionRequiredProjection | null
  appointmentLabel?: string | null
  announcementOverview?: WorkOrderAnnouncementOverview | null
}): WorkOrderActionRequiredProjection | null => {
  if (actionRequired?.reason === "work_order_closed") return actionRequired

  if (`${appointmentLabel ?? ""}`.trim() === "Kein Termin") {
    return { required: true, state: "required", reason: "appointment_missing", priority: 90 }
  }

  if (actionRequired?.reason === "announcement_attention") return actionRequired

  if (announcementOverview?.state === "secured" || announcementOverview?.state === "completed") {
    return {
      required: false,
      state: "not_required",
      priority: 0,
      reason:
        announcementOverview.definition?.value === "CONFIRMED"
          ? "appointment_confirmed"
          : "announcement_completed",
    }
  }

  if (
    actionRequired?.required ||
    ["letter_delivery_pending", "letter_delivery_required"].includes(actionRequired?.reason ?? "")
  )
    return actionRequired ?? null

  if (announcementOverview?.state === "unsecured" || announcementOverview?.state === "failed") {
    return { required: true, state: "required", reason: "announcement_attention", priority: 80 }
  }

  if (actionRequired) return actionRequired

  if (announcementOverview?.state === "no_plan") {
    return {
      required: true,
      state: "required",
      reason: "announcement_plan_missing",
      priority: 80,
      required_at: null,
    }
  }

  if (announcementOverview?.state) {
    return {
      required: false,
      state: "not_required",
      reason: "announcement_running",
      priority: 0,
      required_at: null,
    }
  }

  return null
}

const formatDateTime = (value?: string | null) => {
  if (!value) return null

  const date = new Date(value)

  if (Number.isNaN(date.getTime())) return null

  return date.toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  })
}

const isLetterReason = (reason: WorkOrderActionRequiredReason) =>
  reason === "letter_delivery_required" || reason === "letter_delivery_pending"

const requiredDescriptions: Record<WorkOrderActionRequiredReason, string> = {
  appointment_missing:
    "Für diesen Auftrag fehlt ein aktueller Terminvorschlag. Planen Sie zuerst einen Termin.",
  announcement_plan_missing:
    "Es gibt einen Termin oder Terminvorschlag, aber keinen passenden aktiven Ankündigungsplan.",
  letter_delivery_required:
    "Ein Terminbrief ist fällig und soll innerhalb der Zustellwoche bis Freitag manuell zugestellt werden.",
  letter_delivery_pending:
    "Ein Terminbrief ist als manueller Fallback geplant. Die Aufgabe wird ab Wochenbeginn sichtbar und soll bis Freitag erledigt werden.",
  announcement_attention:
    "Der Ankündigungsplan enthält einen Fehler oder eine Kommunikation, die geprüft werden muss.",
  announcement_completed:
    "Der Ankündigungsplan wurde erfolgreich abgeschlossen. Aktuell ist keine weitere manuelle Aktion notwendig.",
  appointment_confirmed:
    "Der Termin ist bereits vereinbart. Aktuell ist keine manuelle Aktion notwendig.",
  announcement_running: "Die Kommunikation läuft ohne manuelle Aktion weiter.",
  work_order_closed:
    "Der Auftrag ist abgeschlossen oder beendet. Aktuell ist keine manuelle Aktion notwendig.",
  none: "Für diesen Auftrag besteht aktuell kein konkreter Handlungsbedarf.",
}

const reasonSubLabels: Record<WorkOrderActionRequiredReason, string> = {
  appointment_missing: "Terminvorschlag fehlt",
  announcement_plan_missing: "Ankündigungsplan fehlt",
  letter_delivery_required: "Terminbrief zustellen",
  letter_delivery_pending: "Brief als Fallback",
  announcement_attention: "Ankündigung prüfen",
  announcement_completed: "kein Handlungsbedarf",
  appointment_confirmed: "Termin vereinbart",
  announcement_running: "läuft automatisch",
  work_order_closed: "Auftrag abgeschlossen",
  none: "kein Handlungsbedarf",
}

const reasonIcons: Record<WorkOrderActionRequiredReason, string> = {
  appointment_missing: mdiCalendarPlusOutline,
  announcement_plan_missing: mdiClipboardAlertOutline,
  letter_delivery_required: mdiFileDocumentAlertOutline,
  letter_delivery_pending: mdiFileDocumentAlertOutline,
  announcement_attention: mdiAlertCircleOutline,
  announcement_completed: mdiCheckCircleOutline,
  appointment_confirmed: mdiCalendarCheckOutline,
  announcement_running: mdiProgressClock,
  work_order_closed: mdiCheckCircleOutline,
  none: mdiCheckCircleOutline,
}

const nextActionLabel: Record<WorkOrderActionRequiredReason, string> = {
  appointment_missing: "Terminvorschlag erstellen",
  announcement_plan_missing: "Ankündigungsplan anlegen",
  letter_delivery_required: "Terminbrief zustellen",
  letter_delivery_pending: "aktuell keine; ab Zustellwoche Terminbrief zustellen",
  announcement_attention: "Ankündigung prüfen",
  announcement_completed: "keine Aktion erforderlich",
  appointment_confirmed: "keine Aktion erforderlich",
  announcement_running: "keine Aktion erforderlich",
  work_order_closed: "keine Aktion erforderlich",
  none: "keine Aktion erforderlich",
}

const stripEarliestPrefix = (value: string) => value.replace(/^frühestens\s+/i, "")

const communicationLabel = (overview?: WorkOrderAnnouncementOverview | null) =>
  overview?.label ?? null

const communicationDetailsLabel = (announcementOverview?: WorkOrderAnnouncementOverview | null) =>
  announcementOverview?.state === "secured" ? null : announcementOverview?.searchText

const digitalPathLabel = (announcementOverview?: WorkOrderAnnouncementOverview | null) => {
  if (!announcementOverview) return null

  if (announcementOverview.evidence?.hasOpenDigitalStep) return "digitaler Schritt offen"

  if (announcementOverview.evidence?.hasDigitalAnnouncementPath) return "digitaler Schritt im Plan"

  return null
}

const letterStatusLabel = (announcementOverview?: WorkOrderAnnouncementOverview | null) => {
  if (!announcementOverview) return null

  if (announcementOverview.evidence?.hasCompletedLetterStep) {
    return announcementOverview.evidence.completedLetterLabel ?? "Brief zugestellt"
  }

  if (announcementOverview.evidence?.hasOpenLetterStep) return "Brief offen"

  return null
}

const notRequiredSubLabel = (
  reason: WorkOrderActionRequiredReason,
  announcementOverview?: WorkOrderAnnouncementOverview | null,
) => {
  if (reason === "work_order_closed") return "Auftrag abgeschlossen"

  return announcementOverview?.label ?? reasonSubLabels[reason]
}

export const buildActionRequiredOverview = ({
  actionRequired,
  appointmentLabel,
  announcementOverview,
  now,
}: {
  actionRequired?: WorkOrderActionRequiredProjection | null
  appointmentLabel?: string | null
  announcementOverview?: WorkOrderAnnouncementOverview | null
  now?: Date
}): WorkOrderActionRequiredOverview => {
  const effectiveActionRequired = buildMissingProjectionFallback({
    actionRequired,
    appointmentLabel,
    announcementOverview,
  })
  const reason = normalizeReason(effectiveActionRequired?.reason)
  const state = normalizeState(effectiveActionRequired)
  const required = state === "required"
  const label =
    state === "required"
      ? "Erforderlich"
      : state === "not_yet_required"
        ? "Noch nicht"
        : "Nicht erforderlich"
  const letterWindow = buildLetterDeliveryWindowPresentation(
    effectiveActionRequired?.required_at ?? null,
    { now },
  )
  const letterDeliveryIsGuaranteed =
    reason === "letter_delivery_pending" &&
    announcementOverview?.evidence?.guaranteedManualLetterAction === true
  const letterDeliveryHasDigitalPath =
    reason === "letter_delivery_pending" &&
    announcementOverview?.evidence?.hasDigitalAnnouncementPath === true
  const subLabel =
    reason === "letter_delivery_pending"
      ? letterDeliveryIsGuaranteed
        ? stripEarliestPrefix(letterWindow.actionPendingSubLabel)
        : letterWindow.actionPendingSubLabel
      : state === "not_required"
        ? notRequiredSubLabel(reason, announcementOverview)
        : reasonSubLabels[reason]
  const requiredAt = formatDateTime(effectiveActionRequired?.required_at ?? null)
  const description =
    announcementOverview?.state === "unsecured"
      ? "Der Ankündigungsplan ist abgeschlossen. Es liegt aber keine sichere Zustellung oder aktive Kundenbestätigung vor."
      : requiredDescriptions[reason]
  const actionColor =
    state === "required" || announcementOverview?.state === "unsecured" ? "orange" : "neutral"
  const communication = communicationLabel(announcementOverview)
  const communicationDetails = communicationDetailsLabel(announcementOverview)
  const digitalPath = digitalPathLabel(announcementOverview)
  const letterStatus = letterStatusLabel(announcementOverview)
  const definition: StatusDefinition<string> = {
    value: state,
    label,
    color: actionColor,
    icon:
      state === "not_required" && announcementOverview?.state === "unsecured"
        ? mdiProgressClock
        : state === "not_required" &&
            (announcementOverview?.evidence?.hasCompletedLetterStep ||
              (announcementOverview?.state === "secured" &&
                announcementOverview.label === "Gesichert zugestellt"))
          ? mdiEyeCheckOutline
          : state === "not_required"
            ? mdiCheckCircleOutline
            : reasonIcons[reason],
    description,
  }
  const tooltipDescription =
    announcementOverview?.state === "unsecured"
      ? "Die Kontaktversuche sind beendet. Eine Zustellung oder Bestätigung des Kunden ist noch nicht nachgewiesen."
      : reason === "letter_delivery_pending"
        ? letterDeliveryHasDigitalPath && !letterDeliveryIsGuaranteed
          ? "Falls keine Rückmeldung kommt, muss der Termin per Brief angekündigt werden."
          : "Ab Montag der Zustellwoche erscheint die Zustellaufgabe. Bitte den Brief bis Freitag zustellen."
        : reason === "letter_delivery_required"
          ? "Bitte den Brief bis Freitag der Zustellwoche zustellen. Danach als erledigt markieren."
          : reason === "appointment_missing"
            ? "Zuerst einen Termin planen."
            : reason === "announcement_plan_missing"
              ? "Festlegen, wie der Kunde über den Termin informiert werden soll."
              : reason === "announcement_attention"
                ? "Bitte die fehlgeschlagene Kontaktaufnahme in der Terminkommunikation prüfen."
                : ""
  const tooltip = buildRichTooltip([tooltipText(tooltipDescription)])
  const searchText = [
    label,
    subLabel,
    nextActionLabel[reason],
    isLetterReason(reason) ? letterWindow.searchText : requiredAt,
    appointmentLabel,
    announcementOverview?.label,
    announcementOverview?.subLabel,
    communicationDetails,
    digitalPath,
    letterStatus,
    communication,
    description,
  ]
    .filter(Boolean)
    .join(" ")

  return { required, state, reason, label, subLabel, definition, tooltip, searchText }
}
